<script setup lang="ts">
import {computed, onMounted, ref, watch} from 'vue'
import ElementVideo from "@/components/elements/elementVideo.vue";
import WishlistIcon from "@/components/elements/wishlistIcon.vue";
import {useI18n} from "vue-i18n";
import {Config} from "@/assets/ts/config";

const {t} = useI18n()
const pageStr: string = 'components.catalog.catalogItem'

interface Product {
  set_id: string;
  order_idx: number;
  system: string,
  hash?: string;
  stones: string;
  stones_sizes: string;
  metal_short: string;
  metal: string,
  price: number;
  image: {
    filename: string
    filename_layer: string
    main_metal: string
    positions: {
      filename: string
    }[]
  };
  link: string;
}

const props = withDefaults(
    defineProps<{
      product: Product;
      catalogItemType: string;
      index: number;
      hash: string;
      fixImagePosition: string;
      isMinPrice: boolean
    }>(),
    {
      catalogItemType: "",
      index: 0,
      hash: "",
      fixImagePosition: "main",
      isMinPrice: false
    }
);

// wishlist state handled in WishlistIcon component

// Build slider images: main filename + all positions
const images = computed<{ filename: string, filename_layer: string }[]>(() => {
  const base = props.product?.image ? [props.product.image] : [];
  const positions = props.product?.image?.positions ?? [];
  return [...base, ...positions];
});

// Slider state and behavior
const sliderRef = ref<HTMLDivElement | null>(null);
const currentIndex = ref(0);

// Visible dots logic
const displayDots = computed(() => {
  const total = images.value.length;
  if (total === 0) return [];

  // Requirements:
  // - Initially only 3 dots shown.
  // - Max 5 dots.
  // - At the end only 3 dots shown.

  let maxToShow = 5;
  if (currentIndex.value === 0 || currentIndex.value === total - 1) {
    maxToShow = Math.min(total, 3);
  } else if (currentIndex.value === 1 || currentIndex.value === total - 2) {
    maxToShow = Math.min(total, 4);
  } else {
    maxToShow = Math.min(total, 5);
  }

  let start = currentIndex.value - Math.floor(maxToShow / 2);
  if (start < 0) start = 0;
  if (start + maxToShow > total) start = total - maxToShow;

  const dots = [];
  for (let i = 0; i < maxToShow; i++) {
    if (start + i < total) {
      dots.push(start + i);
    }
  }
  return dots;
});

// Clamp index and scroll
function slideTo(index: number) {
  const total = images.value.length;
  if (!sliderRef.value || total === 0) return;
  const clamped = Math.max(0, Math.min(index, total - 1));
  currentIndex.value = clamped;
  const container = sliderRef.value;
  const offset = clamped * container.clientWidth;
  container.scrollTo({left: offset, behavior: 'smooth'});
}

// Sync index with touch scroll
function onScroll() {
  if (!sliderRef.value) return;
  const {scrollLeft, clientWidth} = sliderRef.value;
  const idx = Math.round(scrollLeft / Math.max(clientWidth, 1));
  if (idx !== currentIndex.value) currentIndex.value = idx;
}

function nextSlide() {
  const total = images.value.length;
  if (total === 0) return;

  if (showHoverPreview.value && currentIndex.value + 1 == 1)
    currentIndex.value = currentIndex.value + 1
  showHoverPreview.value = false

  let targetIndex = currentIndex.value + 1;
  if (targetIndex >= total) {
    targetIndex = 0;
  }

  hoverDeactivatedByClick.value = true

  slideTo(targetIndex);
}

function prevSlide() {
  const total = images.value.length;
  if (total === 0) return;

  let targetIndex = currentIndex.value - 1;
  if (targetIndex < 0) {
    targetIndex = total - 1;
  }

  hoverDeactivatedByClick.value = true;

  slideTo(targetIndex);
}

// Desktop hover preview (shows first positions image)
const hoverPreview = computed<string | undefined>(() => props.product?.image?.positions?.[0]);
const showHoverPreview = ref(false);
const hoverDeactivatedByClick = ref(false);

const configuratorLink = computed(() => {
  if (typeof props.product.link != "undefined" && props.product.link !== "") {
    if (props.product.system == 'rubin') {
      let baseUrl = Config.getBaseUrl() || "/"
      return baseUrl + '#/?add=' + props.product.set_id + '/cft_' + props.product.link.toLowerCase()
    } else {
      return "/artikel/" + props.product.set_id.toLowerCase() + "/" + props.product.link.toLowerCase()
    }
  } else {

    let baseUrl = Config.getBaseUrl() || "/"

    return baseUrl + '#/?add=' + props.product.set_id + '/' + props.catalogItemType + props.hash
  }
})
/**
 * fixImagePosition
 */

const scrollToImagePosition = (imagePosition: string) => {
  if (imagePosition === "main") {
    slideTo(0)
  } else if (imagePosition !== "") {
    let foundIdx = 0
    for (const imageIdx in images.value) {
      const imagePositionParts = imagePosition.split("-")
      const cleanImagePosition = imagePositionParts[0]
      const cleanImagePositionIdx = (imagePositionParts.length > 1) ? parseInt(imagePositionParts[1]) : 0
      if (
          images.value[parseInt(imageIdx)]?.filename.includes(cleanImagePosition + "-") ||
          images.value[parseInt(imageIdx)]?.filename.includes("/" + cleanImagePosition + "/")
      ) {
        if (cleanImagePositionIdx === 0) {
          slideTo(parseInt(imageIdx))
          break
        } else {
          foundIdx++
          if (foundIdx == cleanImagePositionIdx) {
            slideTo(parseInt(imageIdx))
            break
          }
        }
      }
    }
  }
}


watch(() => props.fixImagePosition, (newValue: string, oldValue: string) => {
  if (newValue !== oldValue) {
    scrollToImagePosition(newValue)
  }
})

/**
 * on mounted
 */

onMounted(() => {
  /**
   * deactivated
   if (false && props.fixImagePosition == "") {
   setTimeout(() => {
   // Show the "still" image on a staggered pattern (gaps of 7 and 9)
   // so it doesn't align into a straight vertical line in the grid:
   // 3, 10, 19, 26, 35, 42, 51, 58, 67, 74, ...
   const positionInCycle = props.index % 16;
   if (positionInCycle === 3 || positionInCycle === 10) {
   scrollToImagePosition("still")
   }
   }, 200)
   } else {
   scrollToImagePosition(props.fixImagePosition)
   }

   */
  scrollToImagePosition(props.fixImagePosition)
});


</script>

<template>
  <div class="relative cursor-pointer">
    <div class="relative overflow-hidden aspect-square group">
      <div v-if="true" class="absolute top-0 right-0 z-30 p-1">
        <WishlistIcon
            :item-number="props.product.set_id"
            :image="props.product?.image?.filename"
            :link-item-number="configuratorLink"
        />
      </div>

      <!-- Slider -->
      <a :href="configuratorLink">
        <div
            class="relative overflow-hidden bg-white aspect-square z-10 group"
            @mouseover="showHoverPreview = true"
            @mouseleave="showHoverPreview  = false; hoverDeactivatedByClick = false"
        >
          <!-- Slides track -->
          <div
              ref="sliderRef"
              class="h-full w-full flex overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar relative"
              @scroll="onScroll"
          >
            <div
                v-for="(src, i) in images"
                :key="src.filename + '_' + i"
                class="relative flex-shrink-0 w-full h-full snap-center"
            >
              <element-video
                  v-if="src.filename.endsWith('.mp4')"
                  :src=src.filename
                  video-class="mx-auto w-full h-full relative z-30"
              />
              <template v-else>
                <img
                    v-if="catalogItemType.indexOf('wedding_ring') !== -1  && typeof props.product.image.main_metal  != 'undefined' && props.product.image.main_metal != ''"
                    :alt="$t(pageStr + '.main_metal_background')"
                    :src="
            'https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/backgrounds/' +
            props.product.image.main_metal +
            '_' +
            'pos1' +
            '.webp'
          "
                    class="w-full z-10 absolute inset-0 transition-transform duration-75 ease-out"
                />
                <img
                    loading="lazy"
                    :src="src.filename"
                    :alt="props.product.set_id"
                    :title="'(' + props.product.order_idx + ') '+ props.product.set_id"
                    class="relative h-full z-10 w-full transition-transform duration-300 group-hover:scale-105"
                />
                <!-- Optional top layer stays above everything -->
                <img
                    v-if="src.filename_layer"
                    loading="lazy"
                    :src="src.filename_layer"
                    :alt="props.product.set_id"
                    :title="'(' + props.product.order_idx + ') '+ props.product.set_id"
                    class="absolute inset-0 h-full w-full transition-transform duration-300 group-hover:scale-105 z-20 pointer-events-none"
                />
              </template>
            </div>
          </div>

          <!-- Desktop hover: show first positions image on top when hovering main image -->
          <div
              v-if="showHoverPreview && hoverPreview && !hoverDeactivatedByClick"
              :title="'(' + props.product.order_idx + ') '+ props.product.set_id"
              class="hidden md:block absolute inset-0 h-full w-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30"
          >
            <element-video
                v-if="hoverPreview.filename.endsWith('.mp4')"
                :src=hoverPreview.filename
                video-class="mx-auto w-full relative z-30"
            />
            <img
                v-else
                loading="lazy"
                :src="hoverPreview.filename"
                :alt="t(pageStr + '.alt.preview', { id: props.product.set_id })"
                :title="'(' + props.product.order_idx + ') '+ props.product.set_id"
                class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </div>

          <!-- Desktop Arrows -->
          <div
              v-if="images.length > 1"
              class="hidden md:group-hover:flex absolute inset-0 items-center justify-between px-2 z-40 pointer-events-none"
          >
            <button
                class="w-6 h-6 flex items-center justify-center rounded-full bg-white/70 hover:bg-white text-black transition-all pointer-events-auto"
                @click.stop.prevent="prevSlide"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5"
                   stroke="currentColor" class="w-3 h-3">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5"/>
              </svg>
            </button>
            <button
                class="w-6 h-6 flex items-center justify-center rounded-full bg-white/70 hover:bg-white text-black transition-all pointer-events-auto"
                @click.stop.prevent="nextSlide"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5"
                   stroke="currentColor" class="w-3 h-3">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
              </svg>
            </button>
          </div>

        </div>
      </a>
      <!-- Navigation dots -->
      <div class="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1.5 z-30">
        <button
            v-for="i in displayDots"
            :key="i"
            class="w-1.5 h-1.5 rounded-full shadow-sm transition-all duration-300"
            :class="i === currentIndex ? 'bg-black ring-2 ring-gray-300' : 'bg-black/80'"
            @click.stop="slideTo(i)"
        />
      </div>
    </div>
    <a :href="configuratorLink">
      <div class="mt-1 px-1 md:px-0 text-left text-[12px] text-brand-text flex justify-between gap-x-2">
        <template v-if="catalogItemType === 'wedding_ring' && props.isMinPrice">
          <p class="mt-1 whitespace-nowrap font-bold text-[14px] w-full text-center">
            Gold ab
            {{ props.product.price.toLocaleString() }} €
          </p>
        </template>
        <template v-else-if="catalogItemType === 'engagement_ring'">
          <div class="flex gap-x-2 justify-between w-full">
            <p class="mt-1 flex-1 text-left">
              {{ props.product.stones_sizes }}
            </p>
            <p class="mt-1 flex-1 text-center">
              {{ props.product.metal_short }}
            </p>
            <p class="mt-1 flex-1 whitespace-nowrap font-bold text-[14px] text-right">
              {{ props.product.price.toLocaleString() }} €
            </p>
          </div>
        </template>
        <template v-else>
          <div class="md:flex md:gap-x-2">
            <p v-if="false" class="mt-1">
              {{ props.product.stones }}
            </p>
            <p v-if="!props.isMinPrice" class="mt-1">
              {{ props.product.metal }}
            </p>
          </div>
          <p class="mt-1 whitespace-nowrap font-bold text-[14px]">
            {{ props.product.price.toLocaleString() }} €
          </p>
        </template>

      </div>
    </a>
  </div>
</template>

<style scoped>
/* Hide scrollbar for a cleaner slider */
.no-scrollbar {
  -ms-overflow-style: none; /* IE/Edge */
  scrollbar-width: none; /* Firefox */
}

.no-scrollbar::-webkit-scrollbar {
  display: none; /* Chrome/Safari */
}
</style>